// 📊 Usage Dashboard Component
// Shows subscription usage summary and limits

import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { getUsageSummary, ALERT_TYPES } from '../utils/subscriptionLimits';

/**
 * 📊 Usage Progress Bar
 */
const UsageProgressBar = ({ current, limit, unlimited, alertLevel }) => {
  if (unlimited) {
    return (
      <div className="flex items-center space-x-2">
        <div className="flex-1 bg-green-100 rounded-full h-2">
          <div className="bg-green-500 h-full rounded-full" style={{ width: '100%' }} />
        </div>
        <span className="text-xs text-green-600 font-medium">Unlimited</span>
      </div>
    );
  }
  
  const percentage = limit > 0 ? (current / limit) * 100 : 0;
  
  const getColor = () => {
    if (alertLevel === ALERT_TYPES.BLOCKED) return 'bg-red-500';
    if (alertLevel === ALERT_TYPES.CRITICAL) return 'bg-orange-500';
    if (alertLevel === ALERT_TYPES.WARNING) return 'bg-yellow-500';
    return 'bg-blue-500';
  };
  
  const getBgColor = () => {
    if (alertLevel === ALERT_TYPES.BLOCKED) return 'bg-red-100';
    if (alertLevel === ALERT_TYPES.CRITICAL) return 'bg-orange-100';
    if (alertLevel === ALERT_TYPES.WARNING) return 'bg-yellow-100';
    return 'bg-blue-100';
  };
  
  return (
    <div className="flex items-center space-x-2">
      <div className={`flex-1 ${getBgColor()} rounded-full h-2 overflow-hidden`}>
        <div 
          className={`${getColor()} h-full rounded-full transition-all duration-300`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      <span className="text-xs text-gray-600 font-medium min-w-[60px] text-right">
        {current}/{limit} ({Math.round(percentage)}%)
      </span>
    </div>
  );
};

/**
 * 🎯 Feature Usage Card
 */
const FeatureUsageCard = ({ feature }) => {
  const getIcon = () => {
    if (feature.unlimited) {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
    if (feature.alertLevel === ALERT_TYPES.BLOCKED) {
      return <XCircle className="w-5 h-5 text-red-500" />;
    }
    if (feature.alertLevel === ALERT_TYPES.CRITICAL || feature.alertLevel === ALERT_TYPES.WARNING) {
      return <AlertTriangle className="w-5 h-5 text-orange-500" />;
    }
    return <Info className="w-5 h-5 text-blue-500" />;
  };
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          {getIcon()}
          <h4 className="text-sm font-medium text-gray-900">
            {feature.displayName}
          </h4>
        </div>
      </div>
      <UsageProgressBar
        current={feature.current}
        limit={feature.limit}
        unlimited={feature.unlimited}
        alertLevel={feature.alertLevel}
      />
      {feature.alertLevel && !feature.unlimited && (
        <div className="mt-2">
          <span className={`text-xs px-2 py-1 rounded ${
            feature.alertLevel === ALERT_TYPES.BLOCKED ? 'bg-red-100 text-red-800' :
            feature.alertLevel === ALERT_TYPES.CRITICAL ? 'bg-orange-100 text-orange-800' :
            'bg-yellow-100 text-yellow-800'
          }`}>
            {feature.alertLevel === ALERT_TYPES.BLOCKED ? 'Limit Reached' :
             feature.alertLevel === ALERT_TYPES.CRITICAL ? 'Critical' : 'Warning'}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * 📂 Category Section
 */
const CategorySection = ({ categoryName, features }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  const categoryDisplayNames = {
    sales: '💰 Sales',
    purchase: '📦 Purchases',
    payments: '💳 Payments',
    parties: '👥 Parties',
    reports: '📊 Reports',
    storage: '💾 Storage',
    users: '👤 Users'
  };
  
  const displayName = categoryDisplayNames[categoryName] || categoryName;
  
  // Count alerts in this category
  const alertCount = features.filter(f => f.alertLevel && !f.unlimited).length;
  const blockedCount = features.filter(f => f.alertLevel === ALERT_TYPES.BLOCKED).length;
  
  return (
    <div className="mb-6">
      <div 
        className="flex items-center justify-between cursor-pointer mb-3 p-2 hover:bg-gray-50 rounded-lg"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          {displayName}
          {alertCount > 0 && (
            <span className={`ml-2 text-xs px-2 py-1 rounded ${
              blockedCount > 0 ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
            }`}>
              {alertCount} alert{alertCount > 1 ? 's' : ''}
            </span>
          )}
        </h3>
        <ArrowRight className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
      </div>
      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature) => (
            <FeatureUsageCard key={feature.featureKey} feature={feature} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * 📊 Usage Dashboard Component
 */
export const UsageDashboard = ({ companyId, onUpgrade }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  useEffect(() => {
    loadSummary();
  }, [companyId]);
  
  const loadSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!companyId) {
        throw new Error('No company ID provided');
      }
      
      const data = await getUsageSummary(companyId);
      setSummary(data);
      setLastRefresh(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error loading usage summary:', err);
      setError(err.message);
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        <span className="ml-3 text-gray-600">Loading usage data...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <XCircle className="w-5 h-5 text-red-500" />
          <p className="ml-3 text-sm text-red-800">
            Error loading usage data: {error}
          </p>
        </div>
        <button
          onClick={loadSummary}
          className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }
  
  if (!summary) return null;
  
  // Calculate overall stats
  const allFeatures = Object.values(summary.categories).flatMap(cat => cat.features);
  const totalAlerts = allFeatures.filter(f => f.alertLevel && !f.unlimited).length;
  const blockedFeatures = allFeatures.filter(f => f.alertLevel === ALERT_TYPES.BLOCKED).length;
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="w-6 h-6 mr-2 text-blue-500" />
            Usage Dashboard
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Current Plan: <span className="font-semibold">{summary.planName}</span>
            {' • '}
            Period: <span className="font-semibold">{summary.currentMonth}</span>
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadSummary}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          {onUpgrade && (
            <button
              onClick={onUpgrade}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Upgrade Plan
            </button>
          )}
        </div>
      </div>
      
      {/* Stats Summary */}
      {totalAlerts > 0 && (
        <div className={`mb-6 p-4 rounded-lg ${
          blockedFeatures > 0 ? 'bg-red-50 border border-red-200' : 'bg-orange-50 border border-orange-200'
        }`}>
          <div className="flex items-center">
            <AlertTriangle className={`w-5 h-5 ${blockedFeatures > 0 ? 'text-red-500' : 'text-orange-500'}`} />
            <div className="ml-3">
              <p className={`font-medium ${blockedFeatures > 0 ? 'text-red-900' : 'text-orange-900'}`}>
                {blockedFeatures > 0 
                  ? `${blockedFeatures} feature${blockedFeatures > 1 ? 's have' : ' has'} reached the limit`
                  : `${totalAlerts} feature${totalAlerts > 1 ? 's are' : ' is'} approaching the limit`
                }
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Consider upgrading your plan to continue using all features without interruption.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Last Refresh */}
      {lastRefresh && (
        <p className="text-xs text-gray-500 mb-4">
          Last updated: {lastRefresh.toLocaleTimeString()}
        </p>
      )}
      
      {/* Categories */}
      {Object.entries(summary.categories).map(([categoryName, categoryData]) => (
        <CategorySection
          key={categoryName}
          categoryName={categoryName}
          features={categoryData.features}
        />
      ))}
    </div>
  );
};

export default UsageDashboard;

